import {
  type FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
} from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import {
  ActionButton,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormRef,
  useAlertContext,
} from '../../../common/form';
import { useMutationErrors } from '../../../common/queryClient';
import { confirmationHeaders } from '../../../common/utils';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import { AlgorithmJoinsForm } from './AlgorithmJoinsForm';
import { useMutateAlgorithmJoinsQuery } from './query/useMutateAlgorithmJoinsQuery';
import { AlgorithmJoinsSchema } from './schema/AlgorithmJoinsSchema';
import {
  getDefaultAlgorithmJoinFormData,
  isEqualToDefaultAlgorithmJoinFormData,
} from './schema/defaultValues';
import {
  AlgorithmJoinFormDataType,
  AlgorithmJoinsValidFormDataType,
} from './schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import validationTranslations from '../../translations/validation.translations.json';
import { Flex } from 'antd';
import styled from 'styled-components';

const { addTranslations, t } = i18nNsFactory('algorithm-joins-form-modal');
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

interface AlgorithmJoinsFormModalProps
  extends Omit<AlgorithmJoinsFormModalInnerProps, 'isDbSources'> {
  algorithmId: number;
  onClose?: () => void;
  onChange?: () => void;
  joins: AlgorithmJoinFormDataType[];
  actions?: ReactNode;
  transition?: boolean;
  formRef: RefObject<FormRef<AlgorithmJoinsValidFormDataType>>;
}

export const AlgorithmJoinsFormModal: FC<AlgorithmJoinsFormModalProps> = (
  props,
) => {
  const { algorithmId, onClose, onChange, joins, sources, formRef, ...rest } =
    props;

  const { mutateAsync } = useMutateAlgorithmJoinsQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const handleSubmit = useCallback(
    (values: AlgorithmJoinsValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: algorithmId,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(() => {
          onChange?.();
          onClose?.();
          setSuccess();
          formRef.current.supressBlock();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      algorithmId,
      onChange,
      onClose,
      setSuccess,
      setMutationErrors,
      formRef,
    ],
  );

  const isDbSources = useMemo(
    () => sources.some((source) => source.sourceType === 'DB'),
    [sources],
  );

  const initialValues = useMemo(
    () => ({
      joins: joins.length ? joins : getDefaultAlgorithmJoinFormData(),
    }),
    [joins],
  );

  return (
    <Form
      formRef={formRef}
      schema={AlgorithmJoinsSchema.validFormDataSchema}
      defaultValues={initialValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="algorithmJoins"
    >
      <AlgorithmJoinsFormModalInner
        sources={sources}
        isDbSources={isDbSources}
        onClose={onClose}
        {...rest}
      />
    </Form>
  );
};

interface AlgorithmJoinsFormModalInnerProps {
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
  actions?: ReactNode;
  transition?: boolean;
  isDbSources: boolean;
}

const AlgorithmJoinsFormModalInner: FC<AlgorithmJoinsFormModalInnerProps> = (
  props,
) => {
  const {
    sources,
    mappings,
    onClose,
    actions,
    transition = true,
    isDbSources,
  } = props;
  const { isDirty } = useFormState();
  const { getValues } = useFormContext();
  const handleCancel = useCallback(() => {
    if (isDirty && !isEqualToDefaultAlgorithmJoinFormData(getValues('joins'))) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose, getValues]);

  return (
    <Modal
      transitionName={transition ? undefined : ''}
      maskTransitionName={transition ? undefined : ''}
      onCancel={handleCancel}
      width="50rem"
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>{actions}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Container>
        <AlgorithmJoinsForm
          sources={sources}
          mappings={mappings}
          isDbSources={isDbSources}
        />
      </Container>
    </Modal>
  );
};

const Container = styled(Padding)`
  overflow-y: auto;
`;
